import { PureComponent } from "react";
import { PieChart, Pie, Cell, Tooltip, TooltipProps } from "recharts";
import { getNameForOil } from "../../../../common/Oils";

const getRandomBlueGreenColor = () => {
  const hue = Math.random() < 0.5 ? 190 : 130; // 190 for deeper blues, 130 for rich greens
  const saturation = Math.floor(Math.random() * 40) + 30; // Balanced saturation for depth
  const lightness = Math.floor(Math.random() * 20) + 20; // Lower lightness for deeper tones

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const colors = [
  "#2A4D69",
  "#5F8D4E",
  "#3C91E6",
  "#76B041",
  "#4C6B8A",
  "#3F5E5A",
  "#D9A673",
  "#B4C49A",
  "#C9B1BD",
  "#E2CFC3",
  "#CABFAB",
  "#D1BBA8",
  "#B9C3B0",
  "#C5B9A0",
  "#9DA9A0",
  "#C5B9A0",
  "#9DA9A0",
  "#C2B280",
];

interface ExtraTooltipProps extends TooltipProps<number, string> {
  totalValue?: number;
  payload?: any;
}

const CustomTooltip: React.FC<ExtraTooltipProps> = ({
  active,
  payload,
  totalValue,
}) => {
  if (active && payload && payload.length && totalValue) {
    return (
      <div
        style={{
          background: "white",
          padding: "8px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        <p style={{ margin: 0, color: "#333" }}>
          {getNameForOil(payload[0].name)}:{" "}
          <strong>
            {payload[0].value} l (
            {((payload[0].value / totalValue) * 100).toFixed(1)}%)
          </strong>
        </p>
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ data }) => {
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        fontSize: "12px",
      }}
    >
      {data.map((entry, index) => (
        <div key={index} style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: entry.color,
              marginRight: 8,
            }}
          />
          <span>
            {getNameForOil(entry.name)}: {entry.value} l (
            {((entry.value / totalValue) * 100).toFixed(1)}%)
          </span>
        </div>
      ))}
    </div>
  );
};

const formatDataByType = (data) => {
  const typeValues = data.reduce((acc, { type, amount }) => {
    acc[type] = (acc[type] || 0) + amount; // Summing amounts by type
    return acc;
  }, {});

  let colorIndex = 0;

  const getNextColor = () => {
    return colorIndex < colors.length
      ? colors[colorIndex++]
      : getRandomBlueGreenColor();
  };

  return Object.keys(typeValues).map((type) => ({
    name: type,
    value: parseFloat(typeValues[type].toFixed(3)),
    color: getNextColor(),
  }));
};

interface Props {
  data: any;
}
export default class PieChartOilTypes extends PureComponent<Props> {
  render() {
    const { data } = this.props;
    const formattedResults = formatDataByType(data);
    const totalValue = formattedResults.reduce(
      (sum, item) => sum + item.value,
      0,
    );

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
        <PieChart width={140} height={140}>
          <Pie
            data={formattedResults}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={70}
            fill="#8884d8"
            paddingAngle={3}
            dataKey="value"
          >
            {formattedResults.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip totalValue={totalValue} />} />
        </PieChart>
        <CustomLegend data={formattedResults} />
      </div>
    );
  }
}
